import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { gte, isEmpty, isEqual, isNil, lt } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';

import {
    DEFAULT_LANGUAGE,
    HTTP_SUCCESS_STATUS_LOWER_BOUND,
    HTTP_SUCCESS_STATUS_UPPER_BOUND,
} from '@config/global-constants';
import { ENTITY_I18N_FIELD_MAP, IEntityI18nFieldConfig } from '@config/support-user-defined-i18n.config';
import { selectCompanyPrefLang } from '@rpt-ngrx/selectors/company.selector';
import { selectUserLang } from '@rpt-ngrx/selectors/session.selector';

import { NEEDS_X_LOCALES, REQUEST_CALLER_IDENTIFY } from './http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class UserLanguageInterceptor implements HttpInterceptor {
    constructor(private store: Store) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const userLoginPrefLanguage = localStorage.getItem('global/userLocale');
        return combineLatest([this.store.select(selectCompanyPrefLang), this.store.select(selectUserLang)]).pipe(
            take(1),
            concatMap(([companyLang, userLang]) => {
                const locale = userLoginPrefLanguage || userLang || companyLang || DEFAULT_LANGUAGE;
                const callerIdentify = req.context.get(REQUEST_CALLER_IDENTIFY);
                const modifiedReq = req.clone({
                    headers: req.headers.set(
                        req.context.get(NEEDS_X_LOCALES) ? 'x-locales' : 'x-user-language',
                        locale,
                    ),
                });

                if (callerIdentify) {
                    return next.handle(modifiedReq).pipe(
                        map((event) => {
                            const needTranslate =
                                !isNil(event) &&
                                event instanceof HttpResponse &&
                                gte(event.status, HTTP_SUCCESS_STATUS_LOWER_BOUND) &&
                                lt(event.status, HTTP_SUCCESS_STATUS_UPPER_BOUND) &&
                                !isEmpty(event.body);
                            if (needTranslate) {
                                const transformedBody = this.applyI18n(event.body, locale, callerIdentify);
                                return event.clone({ body: transformedBody });
                            }
                            return event;
                        }),
                    );
                }

                return next.handle(modifiedReq);
            }),
        );
    }

    /**
     * Process response data and apply translations from the i18n field to corresponding fields.
     * Example: { name: 'name1', i18n: { fr-CA: { 'name': 'name1-fr-CA' } } }
     * After processing: { name: 'name1-fr-CA', i18n: { ... } }
     * If locale is 'en-US', no processing is done.
     * If translation is not found, append ' [en-US]' to the original value.
     */
    private applyI18n(data: unknown, locale: string, callerIdentify: string): any {
        // If locale is en-US, skip processing
        if (isEqual(locale, DEFAULT_LANGUAGE)) {
            return data;
        }

        if (!data || typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map((item) => this.applyI18n(item, locale, callerIdentify));
        }

        const result = { ...data } as Record<string, any>;

        // Process items array (common pagination response format)
        if (result.items && Array.isArray(result.items)) {
            result.items = result.items.map((item) => this.applyI18n(item, locale, callerIdentify));
        } else {
            this.applyCallerSpecificI18n(result, locale, callerIdentify);
        }

        return result;
    }

    /**
     * Apply translations for the specified fields based on the callerIdentify.
     * @param result The object containing the i18n field.
     * @param locale The target locale for translation.
     * @param callerIdentify The identifier for the caller to determine specific fields.
     */
    private applyCallerSpecificI18n(result: Record<string, unknown>, locale: string, callerIdentify: string): void {
        const fields =
            ENTITY_I18N_FIELD_MAP[callerIdentify] ||
            Object.keys(result?.i18n?.[locale] ?? {}).map((field) => ({ field }));

        for (const fieldConfig of fields) {
            this.applyTranslatedField(result, fieldConfig, locale);
        }
    }

    private applyTranslatedField(
        result: Record<string, unknown>,
        fieldConfig: IEntityI18nFieldConfig,
        locale: string,
    ): void {
        const { field, originField } = fieldConfig;
        const translations = result?.i18n?.[locale];
        const translated = translations?.[field];
        if (originField) {
            result[originField] = result?.[field] || '';
        }

        if (isNil(translations) || isNil(translated)) {
            result[field] = result?.[field] ? `${result[field]} [${DEFAULT_LANGUAGE}]` : '';
            return;
        }

        result[field] = translated;
    }
}
